import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/MoneyInput";
import { DataTable } from "@/components/DataTable";
import {
  UploadCloud, FileText, Trash2, CheckCircle2, AlertTriangle,
  Copy, Loader2, Wand2, Play,
} from "lucide-react";

const KIND_COLOR = {
  QRIS: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  Pulsa: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  Kirim: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  Tarik: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  Game: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  Operasional: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "Perlu ditinjau": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
};

const STATUS_BADGE = {
  ready: { label: "Siap", cls: "bg-emerald-600 text-white" },
  review: { label: "Perlu ditinjau", cls: "bg-yellow-500 text-white" },
  duplicate: { label: "Duplikat", cls: "bg-rose-600 text-white" },
  applied: { label: "Diterapkan", cls: "bg-slate-500 text-white" },
  skipped: { label: "Dilewati", cls: "bg-zinc-400 text-white" },
};

const fmtTgl = (r) => `${r.raw_date} · ${r.raw_time || "--:--"}`;

const SOURCE_APP_KEYS = { gopay: ["gopay"], seabank: ["seabank", "sea bank"], bsi: ["bsi"], dana: ["dana"], dana_ss: ["dana"] };
function matchAppForSource(apps, source) {
  const keys = SOURCE_APP_KEYS[source] || [];
  const norm = (n) => (n || "").toLowerCase().trim();
  return apps.find((a) => keys.includes(norm(a.name)))
    || apps.find((a) => keys.some((k) => new RegExp(`\\b${k}\\b`).test(norm(a.name))))
    || null;
}

export default function ImportGopay() {
  const [apps, setApps] = useState([]);
  const [appId, setAppId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState(null);
  const [images, setImages] = useState([]); // screenshot DANA (banyak gambar)
  const [ssProgress, setSsProgress] = useState(null); // {done,total,current}
  const [fileKey, setFileKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [affectBalance, setAffectBalance] = useState(false);
  const [source, setSource] = useState("gopay");
  const isShot = source === "dana_ss";

  const [batchId, setBatchId] = useState("");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [applying, setApplying] = useState(false);
  const [recalcing, setRecalcing] = useState(false);

  // Aturan nama (operasional / dikecualikan) — berlaku untuk semua sumber impor
  const [nameRules, setNameRules] = useState({ operational: [], excluded: [] });
  const [newOp, setNewOp] = useState("");
  const [newEx, setNewEx] = useState("");
  const [savingRules, setSavingRules] = useState(false);

  const selApp = apps.find((a) => a.id === appId);
  const feeCats = selApp?.fee_categories || [];
  const accounts = selApp?.accounts || [];

  useEffect(() => {
    api.get("/apps").then((r) => setApps(r.data || []));
    loadLatestBatch();
    api.get("/imports/gopay/name-rules")
      .then((r) => setNameRules({ operational: r.data?.operational || [], excluded: r.data?.excluded || [] }))
      .catch(() => {});
  }, []);

  const saveRules = async (next) => {
    setSavingRules(true);
    try {
      const r = await api.put("/imports/gopay/name-rules", next);
      setNameRules({ operational: r.data?.operational || [], excluded: r.data?.excluded || [] });
      toast.success("Aturan nama disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan aturan");
    } finally {
      setSavingRules(false);
    }
  };
  const addName = (kind) => {
    const val = (kind === "operational" ? newOp : newEx).trim();
    if (!val) return;
    const cur = nameRules[kind] || [];
    if (cur.some((x) => x.toLowerCase() === val.toLowerCase())) {
      kind === "operational" ? setNewOp("") : setNewEx("");
      return;
    }
    const next = { ...nameRules, [kind]: [...cur, val] };
    kind === "operational" ? setNewOp("") : setNewEx("");
    saveRules(next);
  };
  const removeName = (kind, val) => {
    const next = { ...nameRules, [kind]: (nameRules[kind] || []).filter((x) => x !== val) };
    saveRules(next);
  };

  // Otomatis pilih aplikasi sesuai sumber (nama cocok). Rekening TIDAK dipilih otomatis.
  useEffect(() => {
    const app = matchAppForSource(apps, source);
    setAppId(app ? app.id : "");
  }, [source, apps.length]);

  useEffect(() => {
    // rekening wajib dipilih manual setiap aplikasi berubah
    setAccountId("");
  }, [appId]);

  const autoCreate = (source === "seabank" || source === "dana" || source === "dana_ss") && !appId;

  const loadLatestBatch = async () => {
    try {
      const r = await api.get("/imports/gopay/batches");
      const b = (r.data || []).find((x) => x.ready + x.review > 0) || (r.data || [])[0];
      if (b) {
        setBatchId(b.batch_id);
        await loadRows(b.batch_id);
      }
    } catch (e) { /* ignore */ }
  };

  const loadRows = async (bid) => {
    setLoading(true);
    try {
      const r = await api.get("/imports/gopay/rows", { params: { batch_id: bid } });
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  const doUploadScreenshots = async () => {
    if (!images.length) return toast.error("Pilih screenshot");
    if (!appId && !autoCreate) return toast.error("Pilih aplikasi tujuan");
    if (appId && accounts.length > 0 && !accountId) return toast.error(`Pilih rekening ${selApp?.name || ""} terlebih dahulu`);
    setUploading(true);
    const all = [];
    let partial = 0;
    const failed = [];
    try {
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        setSsProgress({ done: i, total: images.length, current: img.name });
        try {
          const fd = new FormData();
          fd.append("file", img);
          const r = await api.post("/imports/gopay/dana-screenshot/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
          all.push(...(r.data.rows || []));
          partial += r.data.partial || 0;
          if (!(r.data.rows || []).length) failed.push(img.name);
        } catch (e) {
          failed.push(img.name);
        }
      }
      setSsProgress({ done: images.length, total: images.length, current: "Menggabungkan & mengurutkan…" });
      if (!all.length) {
        toast.error("Tidak ada transaksi terbaca dari screenshot. Pastikan screenshot halaman Aktivitas DANA.");
        return;
      }
      const r = await api.post("/imports/gopay/dana-screenshot/batch", {
        rows: all, app_id: appId || null, account_id: accountId || null,
      });
      setSummary(r.data);
      setBatchId(r.data.batch_id);
      await loadRows(r.data.batch_id);
      toast.success(`Terbaca ${r.data.total_saldo} transaksi dari ${images.length} gambar · ${r.data.duplicates_removed || 0} duplikat dihapus`
        + (partial ? ` · ${partial} baris terpotong dilewati` : ""));
      if (failed.length) toast.warning(`Tidak ada transaksi terbaca di: ${failed.join(", ")}`);
      setImages([]);
      setFileKey((k) => k + 1);
      const ar = await api.get("/apps");
      setApps(ar.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membaca screenshot");
    } finally {
      setUploading(false);
      setSsProgress(null);
    }
  };

  const doUpload = async () => {
    if (isShot) return doUploadScreenshots();
    if (!file) return toast.error("Pilih file PDF");
    if (!appId && !autoCreate) return toast.error("Pilih aplikasi tujuan");
    if (appId && accounts.length > 0 && !accountId) return toast.error(`Pilih rekening ${selApp?.name || ""} terlebih dahulu`);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      if (appId) {
        fd.append("app_id", appId);
        if (accountId) fd.append("account_id", accountId);
      }
      const r = await api.post("/imports/gopay/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(r.data);
      setBatchId(r.data.batch_id);
      await loadRows(r.data.batch_id);
      const skipLbl = source === "seabank" ? "baris dilewati (Bunga/Jago)"
        : source === "bsi" ? "baris dilewati (Bagi Hasil/dikecualikan)"
        : source === "dana" ? "baris dilewati"
          : "baris Coins dilewati";
      toast.success(`Terbaca ${r.data.total_saldo} transaksi · ${r.data.coins_skipped} ${skipLbl}`);
      setFile(null);
      // refresh apps (Seabank mungkin baru dibuat)
      const ar = await api.get("/apps");
      setApps(ar.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah");
    } finally {
      setUploading(false);
    }
  };

  const patchRow = async (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      const r = await api.patch(`/imports/gopay/rows/${id}`, patch);
      setRows((prev) => prev.map((x) => (x.id === id ? r.data : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan");
      loadRows(batchId);
    }
  };

  const setKind = (row, kind) => {
    if (kind === "tarik") patchRow(row.id, { flow: "tarik", transfer_category: "", is_operational: false });
    else if (kind === "operational") patchRow(row.id, { is_operational: true });
    else patchRow(row.id, { flow: "kirim", is_operational: false });
  };

  const kindOf = (r) => (r.is_operational ? "operational" : r.flow === "tarik" ? "tarik" : "kirim");

  const delRow = async (id) => {
    try {
      await api.delete(`/imports/gopay/rows/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const doApply = async () => {
    if (!batchId) return;
    setApplying(true);
    try {
      const r = await api.post("/imports/gopay/apply", { batch_id: batchId, affect_balance: affectBalance });
      const d = r.data;
      toast.success(`${d.created} transaksi diterapkan${d.overwritten ? ` · ${d.overwritten} ditimpa` : ""}${d.error_count ? ` · ${d.error_count} gagal` : ""}`);
      if (d.error_count) d.errors.slice(0, 3).forEach((e) => toast.error(e));
      await loadRows(batchId);
      // refresh app balances
      const ar = await api.get("/apps");
      setApps(ar.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menerapkan");
    } finally {
      setApplying(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: rows.length, ready: 0, review: 0, skipped: 0, applied: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const visible = useMemo(() => (filter === "all" ? rows : rows.filter((r) => r.status === filter)), [rows, filter]);
  const selectedCount = rows.filter((r) => r.selected && (r.status === "ready" || r.status === "review")).length;
  const selectableVisible = visible.filter((r) => r.status === "ready" || r.status === "review");
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((r) => r.selected);

  const toggleAllVisible = () => {
    const target = !allVisibleSelected;
    const ids = new Set(selectableVisible.map((r) => r.id));
    setRows((prev) => prev.map((r) => (ids.has(r.id) ? { ...r, selected: target } : r)));
    selectableVisible.forEach((r) => api.patch(`/imports/gopay/rows/${r.id}`, { selected: target }).catch(() => {}));
  };

  const recalcAll = async () => {
    const targets = rows.filter((r) => r.status !== "applied" && r.status !== "skipped");
    if (!targets.length) return;
    setRecalcing(true);
    try {
      await Promise.all(targets.map((r) =>
        api.patch(`/imports/gopay/rows/${r.id}`, { recalc_fee: true })
          .then((res) => setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, ...res.data } : x)))
          .catch(() => {})));
      toast.success("Untung dihitung ulang otomatis (harga bulat pelanggan)");
    } finally {
      setRecalcing(false);
    }
  };

  // opsi kategori: gabungan kategori aplikasi batch + yang muncul di baris + default
  const catOptions = useMemo(() => {
    const set = new Set();
    const batchApp = apps.find((a) => rows[0] && a.id === rows[0].app_id);
    (batchApp?.fee_categories || feeCats || []).forEach((c) => set.add(c.name));
    rows.forEach((r) => { if (r.transfer_category) set.add(r.transfer_category); });
    ["QRIS", "Pulsa", "Sesama Gopay", "Dana", "GoPay", "Transfer Bank", "E-Wallet"].forEach((c) => set.add(c));
    return [...set];
  }, [apps, rows, feeCats]);

  const columns = useMemo(() => [
    {
      key: "sel",
      label: (
        <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
          data-testid="import-select-all" className="h-4 w-4 align-middle" />
      ),
      render: (r) => {
        const locked = r.status === "applied" || r.status === "skipped";
        return <input type="checkbox" className="h-4 w-4" disabled={locked}
          checked={!!r.selected} onChange={(e) => patchRow(r.id, { selected: e.target.checked })} />;
      },
    },
    {
      key: "tgl", label: "Tgl / Jam",
      render: (r) => {
        const locked = r.status === "applied" || r.status === "skipped";
        return (
          <div className="whitespace-nowrap font-mono text-xs space-y-0.5">
            <div>{r.raw_date}</div>
            {r.source === "seabank" ? (
              <input type="time" value={r.raw_time || ""} disabled={locked}
                onChange={(e) => patchRow(r.id, { raw_time: e.target.value })}
                className="h-7 w-[92px] rounded border border-input bg-background px-1 text-xs" />
            ) : (
              <div className="text-muted-foreground">{r.raw_time || "--:--"}</div>
            )}
          </div>
        );
      },
    },
    {
      key: "desc", label: "Deskripsi",
      render: (r) => (
        <div className="min-w-[180px] max-w-[280px]">
          <div className="break-words">{r.raw_desc}</div>
          <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${KIND_COLOR[r.kind_label] || "bg-muted text-foreground/70"}`}>{r.kind_label}</span>
        </div>
      ),
    },
    {
      key: "jenis", label: "Jenis",
      render: (r) => {
        const locked = r.status === "applied" || r.status === "skipped";
        return (
          <select value={kindOf(r)} disabled={locked} onChange={(e) => setKind(r, e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            <option value="kirim">Kirim/Beli</option>
            <option value="tarik">Tarik</option>
            <option value="operational">Operasional</option>
          </select>
        );
      },
    },
    {
      key: "kategori", label: "Kategori",
      render: (r) => {
        const kind = kindOf(r);
        const locked = r.status === "applied" || r.status === "skipped";
        return (
          <select value={r.transfer_category || ""} disabled={locked || kind !== "kirim"}
            onChange={(e) => patchRow(r.id, { transfer_category: e.target.value })}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs min-w-[130px] disabled:opacity-50">
            <option value="">{kind === "tarik" ? "(tanpa kategori)" : "— pilih —"}</option>
            {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        );
      },
    },
    {
      key: "nominal", label: "Nominal (mutasi)", align: "right",
      render: (r) => {
        const locked = r.status === "applied" || r.status === "skipped";
        return <MoneyInput value={r.nominal} disabled={locked}
          onChange={(v) => setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, nominal: v } : x))}
          onBlur={() => patchRow(r.id, { nominal: Number(r.nominal) || 0 })} className="h-8 text-right text-xs min-w-[110px]" />;
      },
    },
    {
      key: "bayar", label: "Bayar Pelanggan", align: "right",
      render: (r) => {
        const locked = r.status === "applied" || r.status === "skipped";
        const bayar = Math.round((Number(r.nominal) || 0) + (Number(r.biaya_admin) || 0));
        return <MoneyInput value={bayar} disabled={locked || r.is_operational}
          onChange={(v) => setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, biaya_admin: (Number(v) || 0) - (Number(x.nominal) || 0) } : x))}
          onBlur={() => patchRow(r.id, { biaya_admin: Number(r.biaya_admin) || 0 })} className="h-8 text-right text-xs min-w-[120px]" />;
      },
    },
    {
      key: "untung", label: "Untung", align: "right",
      render: (r) => {
        if (r.is_operational) return <span className="text-xs text-muted-foreground">-</span>;
        const u = Math.round(Number(r.biaya_admin) || 0);
        return <span className={`font-semibold text-xs whitespace-nowrap ${u < 0 ? "text-rose-600" : "text-emerald-600"}`}>{rupiah(u)}</span>;
      },
    },
    {
      key: "potongan", label: "Potongan", align: "right",
      render: (r) => {
        const locked = r.status === "applied" || r.status === "skipped";
        return <MoneyInput value={r.potongan || 0} disabled={locked}
          onChange={(v) => setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, potongan: v } : x))}
          onBlur={() => patchRow(r.id, { potongan: Number(r.potongan) || 0 })} className="h-8 text-right text-xs min-w-[90px]" />;
      },
    },
    {
      key: "status", label: "Status",
      render: (r) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[r.status]?.cls || "bg-muted"}`}>
          {r.status === "applied" && <CheckCircle2 className="h-3 w-3" />}
          {r.status === "skipped" && <AlertTriangle className="h-3 w-3" />}
          {STATUS_BADGE[r.status]?.label || r.status}
        </span>
      ),
    },
    {
      key: "aksi", label: "",
      render: (r) => (r.status === "applied" ? null : (
        <button onClick={() => delRow(r.id)} className="text-muted-foreground hover:text-rose-600" title="Hapus baris">
          <Trash2 className="h-4 w-4" />
        </button>
      )),
    },
   
  ], [catOptions, allVisibleSelected, rows]);

  const rowClass = (r) => (r.status === "applied" ? "opacity-60" : r.status === "skipped" ? "opacity-60 bg-muted/30" : "");

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" /> Impor Mutasi
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Unggah e-statement <b>GoPay</b> atau rekening koran <b>Seabank</b> (PDF). Sistem mengubahnya jadi baris
          transaksi yang bisa Anda periksa & perbaiki, lalu <b>Terapkan</b> sekali klik. Baris yang tanggal+nominalnya
          sama dengan transaksi yang sudah ada otomatis <b>ditandai duplikat & tidak diinput</b>.
        </p>
        <p className="text-xs text-muted-foreground mt-1 rounded-md bg-accent/40 border px-3 py-2">
          <b>Untung dihitung otomatis</b> dengan asumsi pelanggan bayar <b>angka bulat</b>: kolom <b>Nominal (mutasi)</b> =
          biaya asli yang keluar dari saldo; <b>Bayar Pelanggan</b> = harga bulat (QRIS/Transfer dibulatkan ke bawah kelipatan
          1.000 + admin; Pulsa/GoTagihan dibulatkan ke atas kelipatan 1.000 + admin pulsa); <b>Untung = Bayar − Nominal</b>.
          Semua bisa Anda ubah, atau tekan <b>Hitung ulang otomatis</b> untuk mengembalikan usulan.
        </p>
      </div>

      {/* Upload card */}
      <Card className="p-4 lg:p-5 space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Sumber</label>
            <select data-testid="import-source-select" value={source} onChange={(e) => setSource(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="gopay">GoPay (e-statement)</option>
              <option value="seabank">Seabank (rekening koran)</option>
              <option value="bsi">BSI (rekening koran)</option>
              <option value="dana">DANA (Riwayat Aktivitas)</option>
              <option value="dana_ss">DANA (Screenshot riwayat)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Aplikasi tujuan <span className="text-[10px] text-emerald-600">(otomatis)</span></label>
            <select data-testid="import-app-select" value={appId} onChange={(e) => setAppId(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              {!appId && (
                <option value="">
                  {source === "seabank" ? "(Buat otomatis: Seabank)" : (source === "dana" || source === "dana_ss") ? "(Buat otomatis: DANA)" : "— Pilih aplikasi —"}
                </option>
              )}
              {apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Rekening <span className="text-[10px] text-amber-600">(pilih manual)</span></label>
            <select data-testid="import-account-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}
              className={`w-full h-10 rounded-md border bg-background px-3 text-sm ${appId && accounts.length > 0 && !accountId ? "border-amber-500 ring-1 ring-amber-500/40" : "border-input"}`}
              disabled={!appId || accounts.length === 0}>
              {!appId ? (
                <option value="">{autoCreate ? "(dibuat otomatis dari PDF)" : "— pilih aplikasi dulu —"}</option>
              ) : accounts.length === 0 ? (
                <option value="">(aplikasi tanpa rekening)</option>
              ) : (
                <option value="">— Pilih rekening —</option>
              )}
              {accounts.map((ac) => (
                <option key={ac.id} value={ac.id}>
                  {ac.label}{ac.account_number ? ` (${ac.account_number})` : ""} · {rupiah(ac.saldo)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{isShot ? "Screenshot (boleh banyak)" : "File PDF"}</label>
            {isShot ? (
              <Input key={`ss-${fileKey}`} data-testid="import-images-input" type="file" multiple
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setImages(Array.from(e.target.files || []))} className="h-10" />
            ) : (
              <Input key={`pdf-${fileKey}`} data-testid="import-file-input" type="file" accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-10" />
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="import-upload-btn" onClick={doUpload} disabled={uploading || (isShot ? images.length === 0 : !file)} className="h-10">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            <span className="ml-2">{isShot ? `Baca ${images.length || ""} Screenshot` : "Unggah & Baca"}</span>
          </Button>
          <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2 flex-1 min-w-[280px]">
            <Switch data-testid="import-affect-balance" checked={affectBalance} onCheckedChange={setAffectBalance} />
            <div className="text-sm">
              <span className="font-medium">{affectBalance ? "Mempengaruhi saldo aplikasi" : "Data historis (tidak mengubah saldo)"}</span>
              <p className="text-xs text-muted-foreground">
                {affectBalance
                  ? "Saat diterapkan, saldo & kas ikut berubah sesuai transaksi."
                  : "Direkomendasikan untuk data bulan lalu — untung tetap masuk laporan, saldo sekarang tidak diubah."}
              </p>
            </div>
          </div>
        </div>
        {isShot && (
          <div className="space-y-2" data-testid="import-ss-info">
            <p className="text-xs text-muted-foreground rounded-lg bg-muted/50 px-3 py-2.5">
              <b>Screenshot DANA:</b> buka DANA → <b>Aktivitas</b>, screenshot daftar transaksi (gulir & screenshot lagi, boleh tumpang tindih).
              Pilih semua gambar sekaligus. Dibaca dengan <b>OCR lokal (tanpa AI)</b>. Transaksi <b>diurutkan per tanggal & jam</b>,
              duplikat (tanggal+jam+nama+nominal sama) <b>dihapus otomatis</b>, baris yang terpotong di tepi gambar dilewati.
              Terima Uang → Tarik, Kirim Uang → Transfer DANA, ikon QRIS → QRIS, lainnya → Perlu ditinjau.
            </p>
            {images.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {images.map((f, i) => (
                  <Badge key={i} variant="secondary" className="font-normal">{i + 1}. {f.name}</Badge>
                ))}
              </div>
            )}
            {ssProgress && (
              <div className="rounded-lg border px-3 py-2 text-xs space-y-1.5" data-testid="import-ss-progress">
                <div className="flex justify-between">
                  <span>Membaca gambar {Math.min(ssProgress.done + 1, ssProgress.total)}/{ssProgress.total} · {ssProgress.current}</span>
                  <span className="font-mono">{Math.round((ssProgress.done / ssProgress.total) * 100)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${(ssProgress.done / ssProgress.total) * 100}%` }} />
                </div>
              </div>
            )}
          </div>
        )}
        {source === "bsi" && (
          <p className="text-xs text-muted-foreground rounded-lg bg-muted/50 px-3 py-2.5">
            <b>BSI (rekening koran):</b> pilih aplikasi & akun tujuan di atas (tidak dibuat otomatis).
            Jenis transaksi dikenali otomatis: <b>QRIS</b>, <b>Transfer/BI-Fast</b> (Tarik bila masuk, Kirim bila keluar),
            <b> top up e-wallet</b> (DANA/GoPay), <b>Pulsa</b>. <b>LKP Fee</b> → <b>Bonus/Pemasukan</b>;
            <b> Biaya Administrasi</b> → <b>Operasional</b> (beban); <b>Bagi Hasil</b> dilewati.
            <b> Potongan = biaya asli dari mutasi</b> (mis. BI-Fast, top up) — beda per bank; bila tak tercantum,
            mengikuti aturan aplikasi. Untung mengikuti tarif menu Transaksi (tabel potongan bila antar bank).
          </p>
        )}
      </Card>

      {/* Kelola nama: operasional & dikecualikan (berlaku semua sumber) */}
      <Card className="p-4 lg:p-5 space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Aturan Nama Impor</h3>
          {savingRules && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <p className="text-xs text-muted-foreground -mt-1">
          Berlaku untuk semua sumber (GoPay/Seabank/BSI). Cocok secara teks pada keterangan mutasi (tidak peka huruf besar/kecil).
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-xs font-medium text-orange-700 dark:text-orange-300">
              Nama Operasional <span className="text-muted-foreground">(transaksi untuk kebutuhan toko sendiri)</span>
            </label>
            <div className="flex gap-2">
              <Input data-testid="rule-op-input" value={newOp} onChange={(e) => setNewOp(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addName("operational"); }}
                placeholder="mis. abdul muis" className="h-9" />
              <Button data-testid="rule-op-add" variant="outline" className="h-9" onClick={() => addName("operational")}>Tambah</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(nameRules.operational || []).length === 0 && <span className="text-xs text-muted-foreground">Belum ada.</span>}
              {(nameRules.operational || []).map((nm) => (
                <Badge key={nm} variant="secondary" className="gap-1 bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
                  {nm}
                  <button data-testid={`rule-op-del-${nm}`} onClick={() => removeName("operational", nm)} className="ml-0.5 hover:text-rose-600">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-rose-700 dark:text-rose-300">
              Nama Dikecualikan <span className="text-muted-foreground">(tidak diimpor)</span>
            </label>
            <div className="flex gap-2">
              <Input data-testid="rule-ex-input" value={newEx} onChange={(e) => setNewEx(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addName("excluded"); }}
                placeholder="mis. lisa safriana" className="h-9" />
              <Button data-testid="rule-ex-add" variant="outline" className="h-9" onClick={() => addName("excluded")}>Tambah</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(nameRules.excluded || []).length === 0 && <span className="text-xs text-muted-foreground">Belum ada.</span>}
              {(nameRules.excluded || []).map((nm) => (
                <Badge key={nm} variant="secondary" className="gap-1 bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
                  {nm}
                  <button data-testid={`rule-ex-del-${nm}`} onClick={() => removeName("excluded", nm)} className="ml-0.5 hover:text-rose-600">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {summary && (
        <div className="text-sm text-muted-foreground">
          Terbaca <b className="text-foreground">{summary.total_saldo}</b> transaksi ·{" "}
          <b className="text-foreground">{summary.coins_skipped}</b>{" "}
          {summary.source === "seabank" ? "baris dilewati (Bunga / JAGO / dikecualikan)"
            : summary.source === "dana" ? "baris dilewati (dikecualikan)"
            : summary.source === "bsi" ? "baris dilewati (Bagi Hasil / dikecualikan)"
            : "baris GoPay Coins dilewati otomatis"}
          {summary.duplicates_removed != null && (
            <> · <b className="text-foreground">{summary.duplicates_removed}</b> duplikat antar-screenshot dihapus</>
          )}.
        </div>
      )}

      {rows.length > 0 && (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 justify-between sticky top-0 z-10 bg-background/80 backdrop-blur py-2">
            <div className="flex flex-wrap gap-1.5">
              {[["all", "Semua"], ["ready", "Siap"], ["review", "Perlu ditinjau"], ["skipped", "Dilewati"], ["applied", "Diterapkan"]].map(([k, lbl]) => (
                <button key={k} data-testid={`import-filter-${k}`} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filter === k ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent border-border"}`}>
                  {lbl} <span className="opacity-70">({counts[k] || 0})</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" data-testid="import-recalc-btn" onClick={recalcAll} disabled={recalcing}
                title="Hitung ulang untung otomatis: pelanggan bayar harga bulat, untung = bayar - nominal mutasi">
                {recalcing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                <span className="ml-2 hidden sm:inline">Hitung ulang otomatis</span>
              </Button>
              <span className="text-sm text-muted-foreground"><b className="text-foreground">{selectedCount}</b> dipilih</span>
              <Button data-testid="import-apply-btn" onClick={doApply} disabled={applying || selectedCount === 0}
                className="bg-emerald-600 hover:bg-emerald-700">
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                <span className="ml-2">Terapkan ke Transaksi</span>
              </Button>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Memuat…
            </Card>
          ) : (
            <DataTable
              testid="import-table"
              columns={columns}
              rows={visible}
              rowClassName={rowClass}
              zoomable
              emptyText="Tidak ada baris pada filter ini."
            />
          )}
        </>
      )}

      {rows.length === 0 && !loading && (
        <Card className="p-10 text-center text-muted-foreground">
          <Wand2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>Belum ada data impor. Unggah PDF (GoPay / Seabank) untuk memulai.</p>
        </Card>
      )}
    </div>
  );
}
